import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import GojuonSettings from '../components/GojuonSettings';
import LanguageSwitcher from '../components/LanguageSwitcher';

const GameContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 40px;
    background: #1a1a3e;
    color: #f4d4a3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const GameWrapper = styled.div`
    max-width: 800px;
    width: 100%;
    text-align: center;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 10px;
    font-weight: 700;
    color: #f4d4a3;
    text-shadow: 0 2px 10px rgba(244, 212, 163, 0.3);

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.2rem;
    margin-bottom: 40px;
    opacity: 0.8;
`;

const ModeSelector = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 40px;
    justify-content: center;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SelectContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-width: 180px;
    max-width: 250px;

    @media (max-width: 768px) {
        max-width: 100%;
        min-width: 100%;
    }
`;

const Arrow = styled.div`
    font-size: 2.5rem;
    color: #f4d4a3;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    padding-bottom: 10px;
    opacity: 0.9;
    font-weight: 300;

    @media (max-width: 768px) {
        transform: rotate(90deg);
        padding: 10px 0;
        font-size: 2rem;
    }
`;

const Select = styled.select`
    padding: 15px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 12px;
    color: #f4d4a3;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f4d4a3' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 15px center;
    padding-right: 40px;

    &:hover {
        background-color: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
    }

    &:focus {
        outline: none;
        border-color: rgba(244, 212, 163, 0.6);
        background-color: rgba(244, 212, 163, 0.2);
    }

    option {
        background: #1a1a3e;
        color: #f4d4a3;
    }
`;

const ScoreBoard = styled.div`
    display: flex;
    justify-content: space-around;
    margin-bottom: 40px;
    padding: 20px;
    background: rgba(244, 212, 163, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(244, 212, 163, 0.2);
`;

const ScoreItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ScoreLabel = styled.span`
    font-size: 0.9rem;
    opacity: 0.7;
`;

const ScoreValue = styled.span`
    font-size: 1.8rem;
    font-weight: 700;
    color: #f4d4a3;
`;

const WordCard = styled.div`
    background: rgba(244, 212, 163, 0.15);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 16px;
    padding: 40px;
    margin-bottom: 40px;
    min-height: 150px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
    }
`;

const WordText = styled.div`
    font-size: 4rem;
    font-weight: 700;
    color: #f4d4a3;
    text-shadow: 0 2px 10px rgba(244, 212, 163, 0.3);
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 3rem;
    }
`;

const WordHint = styled.div`
    font-size: 1rem;
    opacity: 0.6;
    margin-top: 10px;
`;

const OptionsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 40px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const OptionButton = styled.button`
    padding: 25px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 12px;
    color: #f4d4a3;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;

    &:hover:not(:disabled) {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    ${props => props.correct && `
        background: rgba(76, 175, 80, 0.3);
        border-color: rgba(76, 175, 80, 0.6);
    `}

    ${props => props.incorrect && `
        background: rgba(244, 67, 54, 0.3);
        border-color: rgba(244, 67, 54, 0.6);
    `}
`;

const ControlButton = styled.button`
    padding: 15px 40px;
    background: rgba(244, 212, 163, 0.2);
    border: 2px solid rgba(244, 212, 163, 0.4);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    margin: 0 10px;

    &:hover {
        background: rgba(244, 212, 163, 0.3);
        border-color: rgba(244, 212, 163, 0.6);
        transform: translateY(-2px);
    }
`;

const SettingsButton = styled.button`
    position: fixed;
    top: 80px;
    right: 20px;
    padding: 12px 20px;
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 8px;
    color: #f4d4a3;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    z-index: 100;

    &:hover {
        background: rgba(244, 212, 163, 0.2);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        top: 70px;
        right: 15px;
        padding: 10px 16px;
        font-size: 1rem;
    }
`;

const ResultMessage = styled.div`
    padding: 20px;
    margin-bottom: 30px;
    border-radius: 12px;
    font-size: 1.3rem;
    font-weight: 600;

    ${props => props.correct && `
        background: rgba(76, 175, 80, 0.2);
        border: 2px solid rgba(76, 175, 80, 0.4);
        color: #81c784;
    `}

    ${props => props.incorrect && `
        background: rgba(244, 67, 54, 0.2);
        border: 2px solid rgba(244, 67, 54, 0.4);
        color: #ef5350;
    `}
`;

const GameOverScreen = styled.div`
    background: rgba(244, 212, 163, 0.1);
    border: 2px solid rgba(244, 212, 163, 0.3);
    border-radius: 16px;
    padding: 40px;
    margin-top: 40px;
`;

// 五十音数据
const hiraganaData = [
    { hiragana: 'あ', katakana: 'ア', romaji: 'a' },
    { hiragana: 'い', katakana: 'イ', romaji: 'i' },
    { hiragana: 'う', katakana: 'ウ', romaji: 'u' },
    { hiragana: 'え', katakana: 'エ', romaji: 'e' },
    { hiragana: 'お', katakana: 'オ', romaji: 'o' },
    { hiragana: 'か', katakana: 'カ', romaji: 'ka' },
    { hiragana: 'き', katakana: 'キ', romaji: 'ki' },
    { hiragana: 'く', katakana: 'ク', romaji: 'ku' },
    { hiragana: 'け', katakana: 'ケ', romaji: 'ke' },
    { hiragana: 'こ', katakana: 'コ', romaji: 'ko' },
    { hiragana: 'さ', katakana: 'サ', romaji: 'sa' },
    { hiragana: 'し', katakana: 'シ', romaji: 'shi' },
    { hiragana: 'す', katakana: 'ス', romaji: 'su' },
    { hiragana: 'せ', katakana: 'セ', romaji: 'se' },
    { hiragana: 'そ', katakana: 'ソ', romaji: 'so' },
    { hiragana: 'た', katakana: 'タ', romaji: 'ta' },
    { hiragana: 'ち', katakana: 'チ', romaji: 'chi' },
    { hiragana: 'つ', katakana: 'ツ', romaji: 'tsu' },
    { hiragana: 'て', katakana: 'テ', romaji: 'te' },
    { hiragana: 'と', katakana: 'ト', romaji: 'to' },
    { hiragana: 'な', katakana: 'ナ', romaji: 'na' },
    { hiragana: 'に', katakana: 'ニ', romaji: 'ni' },
    { hiragana: 'ぬ', katakana: 'ヌ', romaji: 'nu' },
    { hiragana: 'ね', katakana: 'ネ', romaji: 'ne' },
    { hiragana: 'の', katakana: 'ノ', romaji: 'no' },
    { hiragana: 'は', katakana: 'ハ', romaji: 'ha' },
    { hiragana: 'ひ', katakana: 'ヒ', romaji: 'hi' },
    { hiragana: 'ふ', katakana: 'フ', romaji: 'fu' },
    { hiragana: 'へ', katakana: 'ヘ', romaji: 'he' },
    { hiragana: 'ほ', katakana: 'ホ', romaji: 'ho' },
    { hiragana: 'ま', katakana: 'マ', romaji: 'ma' },
    { hiragana: 'み', katakana: 'ミ', romaji: 'mi' },
    { hiragana: 'む', katakana: 'ム', romaji: 'mu' },
    { hiragana: 'め', katakana: 'メ', romaji: 'me' },
    { hiragana: 'も', katakana: 'モ', romaji: 'mo' },
    { hiragana: 'や', katakana: 'ヤ', romaji: 'ya' },
    { hiragana: 'ゆ', katakana: 'ユ', romaji: 'yu' },
    { hiragana: 'よ', katakana: 'ヨ', romaji: 'yo' },
    { hiragana: 'ら', katakana: 'ラ', romaji: 'ra' },
    { hiragana: 'り', katakana: 'リ', romaji: 'ri' },
    { hiragana: 'る', katakana: 'ル', romaji: 'ru' },
    { hiragana: 'れ', katakana: 'レ', romaji: 're' },
    { hiragana: 'ろ', katakana: 'ロ', romaji: 'ro' },
    { hiragana: 'わ', katakana: 'ワ', romaji: 'wa' },
    { hiragana: 'を', katakana: 'ヲ', romaji: 'wo' },
    { hiragana: 'ん', katakana: 'ン', romaji: 'n' },
];

const GameMode = {
    HIRAGANA_TO_KATAKANA: 'hiragana-to-katakana',
    KATAKANA_TO_HIRAGANA: 'katakana-to-hiragana',
    HIRAGANA_TO_ROMAJI: 'hiragana-to-romaji',
    KATAKANA_TO_ROMAJI: 'katakana-to-romaji',
    ROMAJI_TO_HIRAGANA: 'romaji-to-hiragana',
    ROMAJI_TO_KATAKANA: 'romaji-to-katakana',
};

const WordGamePage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const fromHome = location.state?.fromHome;
    const shouldShowLanguageSwitcher = !fromHome;
    const [gameMode, setGameMode] = useState(GameMode.ROMAJI_TO_HIRAGANA);
    const [fromType, setFromType] = useState('romaji');
    const [toType, setToType] = useState('hiragana');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [shuffledOptions, setShuffledOptions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    // 默认选择所有字符
    const [selectedCharacters, setSelectedCharacters] = useState(() => 
        hiraganaData.map(item => item.hiragana)
    );
    const [questionCount, setQuestionCount] = useState(10);
    const [gameQuestions, setGameQuestions] = useState([]);
    // 是否重做错题
    const [retryMissedQuestions, setRetryMissedQuestions] = useState(false);
    // 错过的题目列表
    const [missedQuestions, setMissedQuestions] = useState([]);
    // 是否隨機順序
    const [randomOrder, setRandomOrder] = useState(true);
    // 保存最新的索引、题目队列、错题队列，避免异步回调读取到旧值
    const currentIndexRef = useRef(currentIndex);
    const gameQuestionsRef = useRef(gameQuestions);
    const missedQuestionsRef = useRef(missedQuestions);

    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    useEffect(() => {
        gameQuestionsRef.current = gameQuestions;
    }, [gameQuestions]);

    useEffect(() => {
        missedQuestionsRef.current = missedQuestions;
    }, [missedQuestions]);

    // 打乱数组
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // 根据选择的字符过滤数据
    const filteredHiraganaData = useMemo(() => 
        hiraganaData.filter(item => 
            selectedCharacters.includes(item.hiragana)
        ),
        [selectedCharacters]
    );

    // 生成遊戲題目列表的函數
    const generateGameQuestions = () => {
        if (filteredHiraganaData.length === 0) return [];
        
        let questionsToUse;
        if (questionCount === 'all' || questionCount >= filteredHiraganaData.length) {
            // 如果選擇全部或題數大於等於可用字符數，使用所有字符
            questionsToUse = randomOrder ? shuffleArray([...filteredHiraganaData]) : [...filteredHiraganaData];
        } else {
            // 否則根據 randomOrder 決定是否打亂，然後選擇指定數量的字符
            const dataToUse = randomOrder ? shuffleArray([...filteredHiraganaData]) : [...filteredHiraganaData];
            questionsToUse = dataToUse.slice(0, questionCount);
        }
        return questionsToUse;
    };

    // 生成选项
    const generateOptions = useCallback((correctAnswer, mode) => {
        const options = [correctAnswer];
        // 使用所有可用的字符來生成選項，而不是只使用遊戲題目中的字符
        const allAnswers = hiraganaData.map(item => {
            switch (mode) {
                case GameMode.HIRAGANA_TO_KATAKANA:
                case GameMode.KATAKANA_TO_HIRAGANA:
                    return mode === GameMode.HIRAGANA_TO_KATAKANA ? item.katakana : item.hiragana;
                case GameMode.HIRAGANA_TO_ROMAJI:
                case GameMode.KATAKANA_TO_ROMAJI:
                    return item.romaji;
                case GameMode.ROMAJI_TO_HIRAGANA:
                case GameMode.ROMAJI_TO_KATAKANA:
                    return mode === GameMode.ROMAJI_TO_HIRAGANA ? item.hiragana : item.katakana;
                default:
                    return '';
            }
        });

        // 从所有答案中随机选择3个错误选项
        const wrongOptions = allAnswers
            .filter(ans => ans !== correctAnswer)
            .sort(() => Math.random() - 0.5)
            .slice(0, 3);

        return shuffleArray([...options, ...wrongOptions]);
    }, []);

    // 计算下一步的队列与状态，保持纯函数便于推理
    const computeNextState = ({ currentIndex, gameQuestions, missedQuestions, retryMissed }) => {
        console.log("computeNextState", { currentIndex, gameQuestions, missedQuestions, retryMissed });
        const hasNextInMain = currentIndex < gameQuestions.length - 1;
        if (hasNextInMain) {
            return {
                nextIndex: currentIndex + 1,
                nextQuestions: gameQuestions,
                clearMissed: false,
                gameOver: false,
            };
        }

        if (retryMissed && missedQuestions.length > 0) {
            // 去重仅限于错题列表自身，允许把同一道题再追加一次以便重做
            const uniqueMissedMap = new Map();
            missedQuestions.forEach(q => {
                if (!uniqueMissedMap.has(q.hiragana)) {
                    uniqueMissedMap.set(q.hiragana, q);
                }
            });
            const uniqueMissed = Array.from(uniqueMissedMap.values());

            return {
                nextIndex: gameQuestions.length,
                nextQuestions: [...gameQuestions, ...uniqueMissed],
                clearMissed: true,
                gameOver: false,
            };
        }

        return {
            nextIndex: currentIndex,
            nextQuestions: gameQuestions,
            clearMissed: false,
            gameOver: true,
        };
    };

    // 初始化题目
    useEffect(() => {
        if (gameStarted && !gameOver && gameMode && gameQuestions.length > 0 && currentIndex < gameQuestions.length) {
            const current = gameQuestions[currentIndex];
            let question, correctAnswer, questionType;

            switch (gameMode) {
                case GameMode.HIRAGANA_TO_KATAKANA:
                    question = current.hiragana;
                    correctAnswer = current.katakana;
                    questionType = 'hiragana';
                    break;
                case GameMode.KATAKANA_TO_HIRAGANA:
                    question = current.katakana;
                    correctAnswer = current.hiragana;
                    questionType = 'katakana';
                    break;
                case GameMode.HIRAGANA_TO_ROMAJI:
                    question = current.hiragana;
                    correctAnswer = current.romaji;
                    questionType = 'hiragana';
                    break;
                case GameMode.KATAKANA_TO_ROMAJI:
                    question = current.katakana;
                    correctAnswer = current.romaji;
                    questionType = 'katakana';
                    break;
                case GameMode.ROMAJI_TO_HIRAGANA:
                    question = current.romaji;
                    correctAnswer = current.hiragana;
                    questionType = 'romaji';
                    break;
                case GameMode.ROMAJI_TO_KATAKANA:
                    question = current.romaji;
                    correctAnswer = current.katakana;
                    questionType = 'romaji';
                    break;
                default:
                    return;
            }

            setCurrentQuestion({ question, correctAnswer, questionType });
            setShuffledOptions(generateOptions(correctAnswer, gameMode));
            setSelectedAnswer(null);
            setIsAnswered(false);
        }
    }, [currentIndex, gameStarted, gameOver, gameMode, gameQuestions, generateOptions]);

    const handleAnswer = (answer) => {
        if (isAnswered || !currentQuestion) return;

        const isCorrect = answer === currentQuestion.correctAnswer;
        setSelectedAnswer(answer);
        setIsAnswered(true);
        setTotalQuestions(prev => prev + 1);

        if (isCorrect) {
            setScore(prev => prev + 1);
        } else {
            // 如果答错了且启用了重做错题功能，记录错题
            // 允许所有错题都被记录，但在添加到 gameQuestions 时会去重
            if (retryMissedQuestions && currentIndex < gameQuestions.length) {
                const current = gameQuestions[currentIndex];
                if (current) {
                    setMissedQuestions(prev => {
                        // 检查是否已经存在相同的错题（使用 hiragana 作为唯一标识）
                        const exists = prev.some(q => q.hiragana === current.hiragana);
                        if (!exists) {
                            return [...prev, current];
                        }
                        return prev;
                    });
                }
            }
        }

        setTimeout(() => {
            // 使用最新的值进行计算，避免闭包拿到旧状态
            const next = computeNextState({
                currentIndex: currentIndexRef.current,
                gameQuestions: gameQuestionsRef.current,
                missedQuestions: missedQuestionsRef.current,
                retryMissed: retryMissedQuestions,
            });

            setGameQuestions(next.nextQuestions);
            setCurrentIndex(next.nextIndex);
            if (next.clearMissed) {
                setMissedQuestions([]);
            }
            if (next.gameOver) {
                setGameOver(true);
            }
        }, 2000);
    };

    const startGame = () => {
        // 如果 gameMode 為 null，根據當前的 fromType 和 toType 計算並設置
        let currentGameMode = gameMode;
        if (!currentGameMode && fromType && toType) {
            const modeMap = {
                'hiragana-katakana': GameMode.HIRAGANA_TO_KATAKANA,
                'hiragana-romaji': GameMode.HIRAGANA_TO_ROMAJI,
                'katakana-hiragana': GameMode.KATAKANA_TO_HIRAGANA,
                'katakana-romaji': GameMode.KATAKANA_TO_ROMAJI,
                'romaji-hiragana': GameMode.ROMAJI_TO_HIRAGANA,
                'romaji-katakana': GameMode.ROMAJI_TO_KATAKANA,
            };
            const modeKey = `${fromType}-${toType}`;
            currentGameMode = modeMap[modeKey];
            if (currentGameMode) {
                setGameMode(currentGameMode);
            }
        }
        if (!currentGameMode || !fromType || !toType) return;
        if (filteredHiraganaData.length === 0) {
            alert(t('wordGame.settings.noCharactersSelected') || '請至少選擇一個字符');
            return;
        }
        // 生成遊戲題目列表
        const questions = generateGameQuestions();
        setGameQuestions(questions);
        setGameStarted(true);
        setGameOver(false);
        setCurrentIndex(0);
        setScore(0);
        setTotalQuestions(0);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setMissedQuestions([]);
    };

    const resetGame = () => {
        setGameStarted(false);
        setGameOver(false);
        setGameMode(GameMode.ROMAJI_TO_HIRAGANA);
        setFromType('romaji');
        setToType('hiragana');
        setCurrentIndex(0);
        setScore(0);
        setTotalQuestions(0);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setMissedQuestions([]);
    };

    // 獲取可用的目標類型選項
    const getAvailableToTypes = (from) => {
        const allTypes = ['hiragana', 'katakana', 'romaji'];
        return allTypes.filter(type => type !== from);
    };

    // 根據 fromType 和 toType 設置 gameMode
    const setModeFromTypes = (from, to) => {
        if (!from || !to) return;
        
        const modeMap = {
            'hiragana-katakana': GameMode.HIRAGANA_TO_KATAKANA,
            'hiragana-romaji': GameMode.HIRAGANA_TO_ROMAJI,
            'katakana-hiragana': GameMode.KATAKANA_TO_HIRAGANA,
            'katakana-romaji': GameMode.KATAKANA_TO_ROMAJI,
            'romaji-hiragana': GameMode.ROMAJI_TO_HIRAGANA,
            'romaji-katakana': GameMode.ROMAJI_TO_KATAKANA,
        };
        
        const modeKey = `${from}-${to}`;
        setGameMode(modeMap[modeKey]);
    };

    // 當 fromType 改變時，重置 toType 和 gameMode
    const handleFromTypeChange = (e) => {
        const newFromType = e.target.value;
        setFromType(newFromType);
        setToType(''); // 重置目標類型
        setGameMode(null); // 重置遊戲模式
    };

    // 當 toType 改變時，設置 gameMode
    const handleToTypeChange = (e) => {
        const newToType = e.target.value;
        setToType(newToType);
        setModeFromTypes(fromType, newToType);
    };

    // 处理设置保存
    const handleSettingsSave = (characters, count, retryMissed, random) => {
        setSelectedCharacters(characters);
        setQuestionCount(count);
        setRetryMissedQuestions(retryMissed !== undefined ? retryMissed : false);
        setRandomOrder(random !== undefined ? random : true);
        // 如果游戏已开始，重置游戏
        if (gameStarted) {
            setGameStarted(false);
            setGameOver(false);
            setCurrentIndex(0);
            setScore(0);
            setTotalQuestions(0);
            setMissedQuestions([]);
        }
    };

    // 选择模式界面（未開始遊戲時顯示）
    if (!gameStarted) {
        const availableToTypes = fromType ? getAvailableToTypes(fromType) : [];
        
        return (
            <GameContainer>
                {shouldShowLanguageSwitcher && <LanguageSwitcher />}
                <SettingsButton onClick={() => setIsSettingsOpen(true)}>
                    ⚙️ {t('wordGame.settings.button')}
                </SettingsButton>
                <GojuonSettings
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    onSave={handleSettingsSave}
                    selectedCharacters={selectedCharacters}
                    questionCount={questionCount}
                    retryMissedQuestions={retryMissedQuestions}
                    randomOrder={randomOrder}
                />
                <GameWrapper>
                    <Title>{t('wordGame.title')}</Title>
                    <Subtitle>{t('wordGame.selectMode')}</Subtitle>
                    <ModeSelector>
                        <SelectContainer>
                            <Select value={fromType} onChange={handleFromTypeChange}>
                                <option value="">{t('wordGame.selectFrom')}</option>
                                <option value="hiragana">{t('wordGame.hiragana')}</option>
                                <option value="katakana">{t('wordGame.katakana')}</option>
                                <option value="romaji">{t('wordGame.romaji')}</option>
                            </Select>
                        </SelectContainer>
                        
                        <Arrow>→</Arrow>
                        
                        <SelectContainer>
                            <Select 
                                value={toType} 
                                onChange={handleToTypeChange}
                                disabled={!fromType}
                            >
                                <option value="">{t('wordGame.selectTo')}</option>
                                {availableToTypes.map(type => (
                                    <option key={type} value={type}>
                                        {type === 'hiragana' && t('wordGame.hiragana')}
                                        {type === 'katakana' && t('wordGame.katakana')}
                                        {type === 'romaji' && t('wordGame.romaji')}
                                    </option>
                                ))}
                            </Select>
                        </SelectContainer>
                    </ModeSelector>
                    
                    <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                        <ControlButton onClick={startGame}>
                            {t('wordGame.startGame')}
                        </ControlButton>
                    </div>
                </GameWrapper>
            </GameContainer>
        );
    }


    // 游戏结束
    if (gameOver) {
        const accuracy = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
        return (
            <GameContainer>
                {shouldShowLanguageSwitcher && <LanguageSwitcher />}
                <SettingsButton onClick={() => setIsSettingsOpen(true)}>
                    ⚙️ {t('wordGame.settings.button')}
                </SettingsButton>
                <GojuonSettings
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    onSave={handleSettingsSave}
                    selectedCharacters={selectedCharacters}
                    questionCount={questionCount}
                    retryMissedQuestions={retryMissedQuestions}
                    randomOrder={randomOrder}
                />
                <GameWrapper>
                    <Title>{t('wordGame.gameOver')}</Title>
                    <GameOverScreen>
                        <ScoreBoard>
                            <ScoreItem>
                                <ScoreLabel>{t('wordGame.finalScore')}</ScoreLabel>
                                <ScoreValue>{score} / {totalQuestions}</ScoreValue>
                            </ScoreItem>
                            <ScoreItem>
                                <ScoreLabel>{t('wordGame.accuracy')}</ScoreLabel>
                                <ScoreValue>{accuracy}%</ScoreValue>
                            </ScoreItem>
                        </ScoreBoard>
                        <div style={{ marginTop: '30px' }}>
                            <ControlButton onClick={startGame}>
                                {t('wordGame.playAgain')}
                            </ControlButton>
                            <ControlButton onClick={resetGame}>
                                {t('wordGame.backToMenu')}
                            </ControlButton>
                        </div>
                    </GameOverScreen>
                </GameWrapper>
            </GameContainer>
        );
    }

    // 计算总题数：当前题目队列长度（包括已添加的错题）+ 待添加的错题数
    // 必须在条件语句之前计算，因为 React Hooks 必须在每次渲染时以相同顺序调用
    const totalQuestionCount = gameQuestions.length + (retryMissedQuestions ? missedQuestions.length : 0);
    // 确保进度条计算与题数显示完全一致：使用已答题数 / 总题数
    const progress = totalQuestionCount > 0 ? Math.min(100, Math.max(0, (totalQuestions / totalQuestionCount) * 100)) : 0;
    // 游戏进行中
    if (!currentQuestion) {
        return null;
    }

    return (
        <GameContainer>
            {shouldShowLanguageSwitcher && <LanguageSwitcher />}
            <SettingsButton onClick={() => setIsSettingsOpen(true)}>
                ⚙️ {t('wordGame.settings.button')}
            </SettingsButton>
            <GojuonSettings
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                onSave={handleSettingsSave}
                selectedCharacters={selectedCharacters}
                questionCount={questionCount}
                retryMissedQuestions={retryMissedQuestions}
                randomOrder={randomOrder}
            />
            <GameWrapper>
                <Title>{t('wordGame.title')}</Title>
                <Subtitle>{t('wordGame.progress', { current: totalQuestions, total: totalQuestionCount })}</Subtitle>

                <ScoreBoard>
                    <ScoreItem>
                        <ScoreLabel>{t('wordGame.score')}</ScoreLabel>
                        <ScoreValue>{score}</ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                        <ScoreLabel>{t('wordGame.questions')}</ScoreLabel>
                        <ScoreValue>{totalQuestions}</ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                        <ScoreLabel>{t('wordGame.accuracy')}</ScoreLabel>
                        <ScoreValue>
                            {totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0}%
                        </ScoreValue>
                    </ScoreItem>
                </ScoreBoard>

                <WordCard>
                    <WordText>{currentQuestion.question}</WordText>
                    <WordHint>
                        {currentQuestion.questionType === 'hiragana' && t('wordGame.hintHiragana')}
                        {currentQuestion.questionType === 'katakana' && t('wordGame.hintKatakana')}
                        {currentQuestion.questionType === 'romaji' && t('wordGame.hintRomaji')}
                    </WordHint>
                </WordCard>

                {isAnswered && (
                    <ResultMessage
                        correct={selectedAnswer === currentQuestion.correctAnswer}
                        incorrect={selectedAnswer !== currentQuestion.correctAnswer}
                    >
                        {selectedAnswer === currentQuestion.correctAnswer
                            ? t('wordGame.correct')
                            : t('wordGame.incorrect', { correct: currentQuestion.correctAnswer })}
                    </ResultMessage>
                )}

                <OptionsContainer>
                    {shuffledOptions.map((option, index) => (
                        <OptionButton
                            key={index}
                            onClick={() => handleAnswer(option)}
                            disabled={isAnswered}
                            correct={isAnswered && option === currentQuestion.correctAnswer}
                            incorrect={isAnswered && option === selectedAnswer && option !== currentQuestion.correctAnswer}
                        >
                            {option}
                        </OptionButton>
                    ))}
                </OptionsContainer>

                <div style={{ marginTop: '20px', width: '100%', background: 'rgba(244, 212, 163, 0.1)', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        background: 'rgba(244, 212, 163, 0.5)',
                        transition: 'width 0.3s ease'
                    }} />
                </div>

                <div style={{ marginTop: '30px' }}>
                    <ControlButton onClick={resetGame}>
                        {t('wordGame.backToMenu')}
                    </ControlButton>
                </div>
            </GameWrapper>
        </GameContainer>
    );
};

export default WordGamePage;
